import type { ClubDirectoryItem } from '@/types/api';
import { BombIcon } from '@/components/clubs/BombIcon';
import { SquidSymbol } from '@/components/clubs/SquidSymbol';
import { normalizeReferralCode } from '@/services/referralCodeService';
import {
  formatStakesLine,
  getPrimaryStakesLevelKey,
  partitionGameTypes,
} from '@/services/stakesLevelService';
import styles from '@/components/clubs/ClubCard.module.css';

interface ClubCardProps {
  club: ClubDirectoryItem;
}

function formatClubName(name: string | null): string {
  return name && name.length > 0 ? name : 'Unnamed Club';
}

function formatPlatform(app: string | null): string {
  if (!app || app.length === 0) {
    return 'Unknown';
  }
  return app;
}

function formatClubId(clubId: string | null): string {
  return clubId && clubId.length > 0 ? clubId : '—';
}

function formatUnion(union: string | null): string {
  return union && union.trim().length > 0 ? union.trim() : '—';
}

function formatNotes(notes: string | null): string {
  if (!notes || notes.trim().length === 0) {
    return 'No notes';
  }
  return notes.trim();
}

function normalizeApp(app: string | null): string {
  return (app ?? '').toUpperCase().replace(/\s+/g, '');
}

function getPlatformBadgeClass(app: string | null): string {
  const normalized = normalizeApp(app);

  if (normalized.includes('CLUBGG')) {
    return styles.platformClubgg ?? styles.platformDefault ?? '';
  }
  if (normalized.includes('POKERBROS')) {
    return styles.platformPokerbros ?? styles.platformDefault ?? '';
  }
  if (normalized.includes('PPPOKER') || normalized === 'PPP') {
    return styles.platformPpp ?? styles.platformDefault ?? '';
  }
  if (normalized.includes('UPOKER')) {
    return styles.platformUpoker ?? styles.platformDefault ?? '';
  }

  return styles.platformDefault ?? '';
}

function getStakesLevelClass(stakesLabels: string[]): string {
  const key = getPrimaryStakesLevelKey(stakesLabels);

  if (key === 'low') {
    return styles.stakesLow ?? '';
  }
  if (key === 'mid') {
    return styles.stakesMid ?? '';
  }
  if (key === 'high') {
    return styles.stakesHigh ?? '';
  }

  return styles.stakesUnknown ?? '';
}

function normalizeGameType(gameType: string): string {
  return gameType.toUpperCase().replace(/\s+/g, '');
}

function isSquidGame(gameType: string): boolean {
  return normalizeGameType(gameType).includes('SQUID');
}

function isBombPot(gameType: string): boolean {
  return normalizeGameType(gameType).includes('BOMB');
}

function usesGameTypeIcon(gameType: string): boolean {
  return isSquidGame(gameType) || isBombPot(gameType);
}

const GAME_TYPE_ICON_SIZE = 24;

function renderGameTypeContent(gameType: string) {
  if (isSquidGame(gameType)) {
    return <SquidSymbol size={GAME_TYPE_ICON_SIZE} />;
  }
  if (isBombPot(gameType)) {
    return <BombIcon size={GAME_TYPE_ICON_SIZE} />;
  }
  return gameType;
}

function getGameTypeIconClass(gameType: string): string {
  if (isSquidGame(gameType)) {
    return styles.gameTypeIconSquid ?? '';
  }
  if (isBombPot(gameType)) {
    return styles.gameTypeIconBomb ?? '';
  }
  return '';
}

function getGameTypeVariant(gameType: string): string {
  const normalized = normalizeGameType(gameType);

  if (normalized.includes('NLH') || normalized === 'HOLDEM') {
    return styles.gameNlh ?? styles.gameDefault ?? '';
  }
  if (normalized.includes('PLO6')) {
    return styles.gamePlo6 ?? styles.gameDefault ?? '';
  }
  if (normalized.includes('PLO5')) {
    return styles.gamePlo5 ?? styles.gameDefault ?? '';
  }
  if (normalized.includes('PLO4') || normalized.includes('PLO') || normalized.includes('OMAHA')) {
    return styles.gamePlo ?? styles.gameDefault ?? '';
  }
  if (normalized.includes('MTT') || normalized.includes('TOURNAMENT')) {
    return styles.gameMtt ?? styles.gameDefault ?? '';
  }
  if (normalized.includes('OFC') || normalized.includes('OPENFACE')) {
    return styles.gameOfc ?? styles.gameDefault ?? '';
  }
  if (normalized.includes('SHORT')) {
    return styles.gameShort ?? styles.gameDefault ?? '';
  }

  return styles.gameDefault ?? '';
}

async function copyToClipboard(value: string): Promise<void> {
  if (value === '—') {
    return;
  }
  await navigator.clipboard.writeText(value);
}

function CopyButton({ value, label }: { value: string; label: string }) {
  return (
    <button
      type="button"
      className={styles.copyButton}
      aria-label={`Copy ${label}`}
      disabled={value === '—'}
      onClick={() => void copyToClipboard(value)}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="9" y="9" width="11" height="11" rx="2" stroke="currentColor" strokeWidth="1.75" />
        <path
          d="M7 15H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v1"
          stroke="currentColor"
          strokeWidth="1.75"
        />
      </svg>
    </button>
  );
}

export function ClubCard({ club }: ClubCardProps) {
  const { stakesLabels, gameTypes: displayGameTypes } = partitionGameTypes(club.gameTypes);
  const gameTypes = displayGameTypes.length > 0 ? displayGameTypes : ['Not listed'];
  const clubId = formatClubId(club.clubId);
  const referralCode = normalizeReferralCode(club.app, club.refNumber);
  const union = formatUnion(club.union);
  const platformLabel = formatPlatform(club.app);
  const platformBadgeClass = getPlatformBadgeClass(club.app);
  const stakesLabel = formatStakesLine(stakesLabels);
  const stakesClass = getStakesLevelClass(stakesLabels);
  const notesText = formatNotes(club.notes);
  const hasNotes = club.notes !== null && club.notes.trim().length > 0;

  return (
    <article className={styles.card}>
      <header className={styles.header}>
        <div className={styles.titleBlock}>
          <h2 className={styles.clubName}>{formatClubName(club.clubName)}</h2>
          <p className={styles.union}>{union}</p>
        </div>
        <span className={`${styles.platformBadge} ${platformBadgeClass}`}>
          {platformLabel.toUpperCase()}
        </span>
      </header>

      <div className={styles.idGrid}>
        <div className={styles.idField}>
          <span className={styles.fieldLabel}>Club ID</span>
          <div className={styles.fieldValueRow}>
            <span className={styles.fieldValue}>{clubId}</span>
            <CopyButton value={clubId} label="club ID" />
          </div>
        </div>
        <div className={styles.idField}>
          <span className={styles.fieldLabel}>Referral Code</span>
          <div className={styles.fieldValueRow}>
            <span className={`${styles.fieldValue} ${styles.referralValue}`}>{referralCode}</span>
            <CopyButton value={referralCode} label="referral code" />
          </div>
        </div>
      </div>

      <section className={styles.section}>
        <h3 className={styles.sectionLabel}>Active Game Types</h3>
        <div className={styles.gameTypes}>
          {gameTypes.map((gameType) =>
            usesGameTypeIcon(gameType) ? (
              <span
                key={gameType}
                className={`${styles.gameTypeIcon} ${getGameTypeIconClass(gameType)}`}
                title={gameType}
                aria-label={gameType}
              >
                {renderGameTypeContent(gameType)}
              </span>
            ) : (
              <span key={gameType} className={`${styles.gameBadge} ${getGameTypeVariant(gameType)}`}>
                {gameType}
              </span>
            ),
          )}
        </div>
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionLabel}>Stakes</h3>
        <p className={`${styles.stakesLine} ${stakesClass}`}>
          <span className={styles.stakesDot} aria-hidden="true" />
          {stakesLabel}
        </p>
      </section>

      <section className={`${styles.section} ${styles.notesSection}`}>
        <h3 className={styles.sectionLabel}>Notes</h3>
        <p className={hasNotes ? styles.notesText : styles.notesEmpty}>{notesText}</p>
      </section>

      <button type="button" className={styles.joinButton}>
        Join Club
      </button>
    </article>
  );
}
